import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Check, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandLogo } from "../components/BrandLogo";
import PhotosStep from "../components/wizard/PhotosStep";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function UploadPhotos() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [draft, setDraft] = useState({ kit_qty: 0, magnets: [] });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const loadedRef = useRef(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/orders/${token}/manage`);
      const d = r.data;
      const arr = new Array(d.kit_qty).fill(null);
      (d.magnets || []).forEach((m, i) => { const idx = (m.slot ? m.slot - 1 : i); if (idx >= 0 && idx < d.kit_qty) arr[idx] = m; });
      setDraft({ kit_qty: d.kit_qty, magnets: arr, order_number: d.order_number });
      setSubmitted(!!d.photos_submitted);
      setState("ok");
      setTimeout(() => { loadedRef.current = true; }, 400);
    } catch {
      setState("error");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Auto-save photos/adjustments to the order (debounced) so nothing is lost.
  useEffect(() => {
    if (!loadedRef.current || submitted) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const magnets = (draft.magnets || []).filter(Boolean).map((m) => ({
        photo_id: m.photo_id, adjusted: !!m.adjusted,
        crop: m.adjusted && m.crop ? { x: m.crop.x, y: m.crop.y, width: m.crop.width, height: m.crop.height } : null,
        rotation: m.rotation || 0, zoom: m.zoom || 1,
      }));
      try { await api.put(`/orders/${token}/photos`, { magnets }); } catch { /* retried on next change */ }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [draft, token, submitted]);

  const filled = (draft.magnets || []).filter(Boolean).length;
  const complete = draft.kit_qty > 0 && filled === draft.kit_qty;

  const submit = async () => {
    setSubmitting(true);
    try {
      // ensure latest save first
      const magnets = (draft.magnets || []).filter(Boolean).map((m) => ({
        photo_id: m.photo_id, adjusted: !!m.adjusted,
        crop: m.adjusted && m.crop ? { x: m.crop.x, y: m.crop.y, width: m.crop.width, height: m.crop.height } : null,
        rotation: m.rotation || 0, zoom: m.zoom || 1,
      }));
      await api.put(`/orders/${token}/photos`, { magnets });
      await api.post(`/orders/${token}/submit`);
      toast.success("Photos submitted for production!");
      navigate(`/track/${token}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit photos.");
      setSubmitting(false);
    }
  };

  if (state === "loading") return <div className="flex min-h-dvh items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (state === "error") return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 text-center">
      <h1 className="font-display text-xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Order not found</h1>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">This link is invalid or has expired.</p>
    </div>
  );

  return (
    <div className="min-h-dvh pb-28">
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/90 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <BrandLogo size={34} />
          <a href={`/track/${token}`} className="text-xs text-[hsl(var(--muted-foreground))] underline underline-offset-4">I'll do this later</a>
        </div>
      </header>
      <div className="mx-auto max-w-xl px-4 py-5">
        <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Add your photos</h1>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Order <b>{draft.order_number}</b> · Photos: {filled} of {draft.kit_qty}</p>
        {submitted ? (
          <div className="mt-6 rounded-xl border bg-card p-5 text-center" data-testid="photos-submitted">
            <Check className="mx-auto h-8 w-8" style={{ color: "hsl(var(--success))" }} />
            <p className="mt-2 text-sm font-medium">Your photos are submitted and ready for production.</p>
            <a href={`/track/${token}`}><Button className="mt-4 h-11 w-full rounded-xl"><MapPin className="mr-1.5 h-4 w-4" /> Track your order</Button></a>
          </div>
        ) : (
          <div className="mt-4"><PhotosStep draft={draft} setDraft={setDraft} /></div>
        )}
      </div>
      {!submitted && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 backdrop-blur">
          <div className="mx-auto max-w-xl px-4 py-3">
            <Button onClick={submit} disabled={!complete || submitting} data-testid="submit-photos-button" className="h-12 w-full rounded-xl text-base">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {complete ? "Submit Photos for Production" : `Add all ${draft.kit_qty} photos to continue`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
